// ============================================================================
// useHealthStats Hook
// Provides health statistics and analytics for children
// ============================================================================

import { useQuery } from '@tanstack/react-query';
import apiClient from '@/core/lib/api-client';

export interface HealthStats {
  totalLogs: number;
  logsThisWeek: number;
  logsThisMonth: number;
  activeMedications: number;
  upcomingAppointments: number;
  missedMedications: number;
  adherenceRate: number;
  seizureFreeDays: number;
  therapySessions: number;
  medicationAdherence: number;
  recentAlerts: HealthAlert[];
  trends: HealthTrend[];
  insights?: Insight[];
}

export interface HealthAlert {
  id: string;
  type: 'warning' | 'info' | 'critical';
  message: string;
  childId: string;
  childName: string;
  createdAt: string;
}

export interface HealthTrend {
  date: string;
  logs: number;
  medications: number;
  appointments: number;
}

export interface Insight {
  id: string;
  type: 'positive' | 'warning' | 'info';
  message: string;
}

interface UseHealthStatsOptions {
  period?: '7d' | '30d' | '90d' | '1y';
  enabled?: boolean;
}

async function fetchHealthStats(childId: string, period: string): Promise<HealthStats> {
  if (!childId) {
    return {
      totalLogs: 0,
      logsThisWeek: 0,
      logsThisMonth: 0,
      activeMedications: 0,
      upcomingAppointments: 0,
      missedMedications: 0,
      adherenceRate: 0,
      seizureFreeDays: 0,
      therapySessions: 0,
      medicationAdherence: 0,
      recentAlerts: [],
      trends: [],
    };
  }

  const response = await apiClient.get(`/children/${childId}/health/stats`, {
    params: { period },
  });
  return response.data;
}

export function useHealthStats(childId: string, options: UseHealthStatsOptions = {}) {
  const { period = '30d', enabled = true } = options;

  return useQuery({
    queryKey: ['health-stats', childId, period],
    queryFn: () => fetchHealthStats(childId, period),
    enabled: enabled && !!childId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: {
      totalLogs: 0,
      logsThisWeek: 0,
      logsThisMonth: 0,
      activeMedications: 0,
      upcomingAppointments: 0,
      missedMedications: 0,
      adherenceRate: 0,
      seizureFreeDays: 0,
      therapySessions: 0,
      medicationAdherence: 0,
      recentAlerts: [],
      trends: [],
    },
  });
}

export default useHealthStats;
